import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Button, Card, Modal, Popconfirm, Popover, Space, Tooltip, notification } from 'antd'
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
import api from '../../api'
import { PortalArtigoModel, PortalEditorSectionTarget } from '../../models/PortalArtigoModel'
import { CtaEditor } from './components/CtaEditor'
import { CoursesEditor } from './components/CoursesEditor'
import { FaqEditor } from './components/FaqEditor'
import { RelatedArticlesEditor } from './components/RelatedArticlesEditor'
import {
  PortalArticleBlockEditor,
  getPortalEditorBlockId,
} from './components/PortalArticleBlockEditor'
import { PortalArticleEditorOutline } from './components/PortalArticleEditorOutline'
import { PortalArticleProblemsPanel } from './components/PortalArticleProblemsPanel'
import { PortalArticleJsonImportModal } from './components/PortalArticleJsonImportModal'
import { PortalEditorTheme, PortalSectionSaveButton } from './components/PortalEditorTheme'
import {
  PortalArticleSettingsSection,
  PortalArticleSettingsSidebar,
} from './components/PortalArticleSettingsSidebar'
import {
  createDefaultCourses,
  createDefaultCta,
  createDefaultFaq,
  createDefaultRelatedArticles,
  createInitialArtigo,
} from './helpers/artigo-defaults'
import { removeSummaryReferencesForAnchors } from './helpers/anchor-utils'
import {
  applyPortalEditorSectionChanges,
  getNormalizedPortalEditorSectionValue,
  getPortalEditorSectionKey,
  isPortalEditorSectionDirty,
  mergeSavedChangesIntoDraft,
  portalEditorSectionExists,
  PortalSectionSaveControlProps,
} from './helpers/editor-section-save'
import { normalizePortalArtigoForSave } from './helpers/artigo-normalize'
import { PortalArticleProblem, validatePortalArtigoBeforeSave } from './helpers/artigo-validation'
import { downloadPortalArticleJson } from './helpers/portal-article-json-download'
import {
  createPortalArticleJsonExport,
  PortalArticleJsonImportResult,
} from './helpers/portal-article-json-transfer'
import {
  articleFieldIds,
  getPortalEditorFaqItemId,
  getPortalEditorTopicId,
  getPortalEditorTopicItemId,
  portalEditorSectionIds,
  portalEditorSettingsIds,
} from './helpers/editor-ids'
import { scheduleScrollElementIntoEditorView } from './helpers/editor-scroll'
import {
  isPortalPreviewReadyMessage,
  openPortalArticlePreviewWindow,
  postPortalArticlePreviewPayload,
} from './helpers/preview-window'

export function PortalArtigosEditar() {
  const history = useHistory()
  const params = useParams<{ artigoId?: string }>()
  const isNew = params.artigoId === 'novo' || !params.artigoId
  const [artigo, setArtigo] = useState<PortalArtigoModel>(createInitialArtigo())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedArtigoBaseline, setSavedArtigoBaseline] = useState<PortalArtigoModel>(() =>
    createInitialArtigo()
  )
  const [savingSectionKeys, setSavingSectionKeys] = useState<string[]>([])
  const [sectionSaveErrors, setSectionSaveErrors] = useState<Record<string, string>>({})
  const [previewWindowReady, setPreviewWindowReady] = useState(false)
  const [editingBlockIndexes, setEditingBlockIndexes] = useState<number[]>([])
  const [editingFaqItemIndex, setEditingFaqItemIndex] = useState<number | null>(null)
  const [isFaqExpanded, setIsFaqExpanded] = useState(false)
  const [isCtaExpanded, setIsCtaExpanded] = useState(false)
  const [isCoursesExpanded, setIsCoursesExpanded] = useState(false)
  const [isRelatedArticlesExpanded, setIsRelatedArticlesExpanded] = useState(false)
  const [pendingOptionalSectionCreations, setPendingOptionalSectionCreations] = useState<
    Array<'faq' | 'cta' | 'cursosRelacionados' | 'artigosRelacionados'>
  >([])
  const [tagsInput, setTagsInput] = useState('')
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [jsonImportModalOpen, setJsonImportModalOpen] = useState(false)
  const [openGraphModalOpen, setOpenGraphModalOpen] = useState(false)
  const [expandedSettingsSections, setExpandedSettingsSections] = useState<
    PortalArticleSettingsSection[]
  >(['publish', 'article'])
  const previewWindowRef = useRef<Window | null>(null)
  const savingSectionKeysRef = useRef<Set<string>>(new Set())
  const validationProblems = useMemo(() => validatePortalArtigoBeforeSave(artigo), [artigo])
  const currentArtigoSnapshot = useMemo(() => getArtigoSnapshot(artigo), [artigo])
  const savedArtigoSnapshot = useMemo(
    () => getArtigoSnapshot(savedArtigoBaseline),
    [savedArtigoBaseline]
  )
  const hasUnsavedChanges = currentArtigoSnapshot !== savedArtigoSnapshot
  const isPillar = artigo.tipo === 'PILLAR'

  const fetchArtigo = useCallback(async () => {
    if (isNew) return
    setLoading(true)
    try {
      const response = await api.PortalArtigos.getById(Number(params.artigoId))
      const loadedArtigo = hydratePortalArtigo(response)
      setArtigo(loadedArtigo)
      setTagsInput(formatTagsInput(response.tags))
      setEditingBlockIndexes([])
      setEditingFaqItemIndex(null)
      setIsFaqExpanded(false)
      setIsCtaExpanded(false)
      setIsCoursesExpanded(false)
      setIsRelatedArticlesExpanded(false)
      setPendingOptionalSectionCreations([])
      setSavedArtigoBaseline(loadedArtigo)
      setSectionSaveErrors({})
    } catch (error) {
      notification.error({ message: error.message || 'Não foi possível carregar o artigo' })
    } finally {
      setLoading(false)
    }
  }, [isNew, params.artigoId])

  useEffect(() => {
    fetchArtigo()
  }, [fetchArtigo])

  const updateArtigo = (patch: Partial<PortalArtigoModel>) => {
    setArtigo((currentArtigo) => ({ ...currentArtigo, ...patch }))
  }

  const navigateToBlock = (blockIndex: number) => {
    setNavigationOpen(false)
    setEditingBlockIndexes((currentIndexes) =>
      currentIndexes.includes(blockIndex)
        ? currentIndexes
        : [...currentIndexes, blockIndex].sort(
            (firstIndex, secondIndex) => firstIndex - secondIndex
          )
    )

    scheduleScrollElementIntoEditorView(() => {
      const block = artigo.conteudo?.[blockIndex]
      return document.getElementById(
        block?.type === 'topic'
          ? getPortalEditorTopicId(block.id)
          : getPortalEditorBlockId(blockIndex)
      )
    })
  }

  const navigateToTopicItem = (blockIndex: number, topicItemIndex: number) => {
    setNavigationOpen(false)
    setEditingBlockIndexes((currentIndexes) =>
      currentIndexes.includes(blockIndex)
        ? currentIndexes
        : [...currentIndexes, blockIndex].sort(
            (firstIndex, secondIndex) => firstIndex - secondIndex
          )
    )

    scheduleScrollElementIntoEditorView(() => {
      const block = artigo.conteudo?.[blockIndex]
      if (block?.type !== 'topic') return null
      const item = block.items[topicItemIndex]
      return document.getElementById(
        getPortalEditorTopicItemId(block.id, item?.id || topicItemIndex)
      )
    })
  }

  const handleBlockCreated = (blockIndex: number) => {
    navigateToBlock(blockIndex)
  }

  const handleProblemClick = (problem: PortalArticleProblem) => {
    if (typeof problem.blockIndex === 'number') {
      if (typeof problem.topicItemIndex === 'number') {
        navigateToTopicItem(problem.blockIndex, problem.topicItemIndex)
      } else {
        navigateToBlock(problem.blockIndex)
      }
      return
    }

    const settingsSection = getProblemSettingsSection(problem)
    if (settingsSection) {
      setExpandedSettingsSections((currentSections) =>
        currentSections.includes(settingsSection)
          ? currentSections
          : [...currentSections, settingsSection]
      )
      if (problem.targetType === 'openGraph') setOpenGraphModalOpen(true)

      scheduleScrollElementIntoEditorView(() => {
        const field = problem.field ? getArticleFieldAndFocus(problem.field) : null
        return field || document.getElementById(portalEditorSettingsIds[settingsSection])
      })
      return
    }

    if (problem.targetType === 'faq') {
      const faqItemIndex = getFaqItemIndexFromProblem(problem)
      setIsFaqExpanded(true)
      if (typeof faqItemIndex === 'number') setEditingFaqItemIndex(faqItemIndex)

      scheduleScrollElementIntoEditorView(() =>
        document.getElementById(
          typeof faqItemIndex === 'number'
            ? getPortalEditorFaqItemId(faqItemIndex)
            : portalEditorSectionIds.faq
        )
      )
      return
    }

    if (problem.targetType === 'cta') {
      setIsCtaExpanded(true)
      scheduleScrollElementIntoEditorView(() => document.getElementById(portalEditorSectionIds.cta))
      return
    }

    if (problem.targetType === 'courses') {
      setIsCoursesExpanded(true)
      scheduleScrollElementIntoEditorView(() =>
        document.getElementById(portalEditorSectionIds.courses)
      )
      return
    }

    if (problem.targetType === 'relatedArticles') {
      setIsRelatedArticlesExpanded(true)
      scheduleScrollElementIntoEditorView(() =>
        document.getElementById(portalEditorSectionIds.relatedArticles)
      )
      return
    }
  }

  const sendPreviewPayload = useCallback(() => {
    const sent = postPortalArticlePreviewPayload(previewWindowRef.current, artigo)
    if (!sent) setPreviewWindowReady(false)
    return sent
  }, [artigo])

  const handlePreview = () => {
    const previewWindow = openPortalArticlePreviewWindow()

    if (!previewWindow) {
      notification.warning({
        message: 'Não foi possível abrir o preview',
        description: 'Verifique se o navegador bloqueou a nova guia.',
      })
      return
    }

    previewWindowRef.current = previewWindow
    setPreviewWindowReady(false)
    postPortalArticlePreviewPayload(previewWindow, artigo)
  }

  const handleDownloadJson = () => {
    try {
      const exported = createPortalArticleJsonExport(artigo)
      downloadPortalArticleJson(exported.text, exported.fileName)
      notification.success({
        message: 'JSON do artigo baixado',
        description: 'O arquivo reflete o conteúdo atual e será importado como rascunho.',
      })
    } catch (error) {
      notification.error({
        message: 'Não foi possível baixar o JSON',
        description: error instanceof Error ? error.message : 'Revise o artigo e tente novamente.',
      })
    }
  }

  const applyImportedJson = ({ artigo: importedArtigo, stats }: PortalArticleJsonImportResult) => {
    const hydratedArtigo = hydratePortalArtigo({
      ...importedArtigo,
      status: 'RASCUNHO',
    })

    setArtigo(hydratedArtigo)
    setTagsInput(formatTagsInput(hydratedArtigo.tags))
    setEditingBlockIndexes([])
    setEditingFaqItemIndex(null)
    setIsFaqExpanded(false)
    setIsCtaExpanded(false)
    setIsCoursesExpanded(false)
    setIsRelatedArticlesExpanded(false)
    setPendingOptionalSectionCreations([])
    setSectionSaveErrors({})
    setNavigationOpen(false)
    setOpenGraphModalOpen(false)
    setExpandedSettingsSections(['publish', 'article'])
    setJsonImportModalOpen(false)

    notification.success({
      message: 'JSON importado no rascunho',
      description: `${hydratedArtigo.titulo || 'Artigo sem título'}: ${stats.rootBlocks} bloco(s), ${stats.topics} tópico(s) e ${stats.images} imagem(ns).${hydratedArtigo.tipo === 'PILLAR' ? ' Tipo Pillar.' : ''} Revise título, slug e imagens antes de salvar.`,
      duration: 7,
    })
  }

  const handleImportJson = (result: PortalArticleJsonImportResult) => {
    if (!hasUnsavedChanges) {
      applyImportedJson(result)
      return
    }

    Modal.confirm({
      title: 'Substituir o rascunho atual?',
      content: 'A importação substituirá todas as alterações locais ainda não salvas.',
      okText: 'Substituir e importar',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: () => applyImportedJson(result),
    })
  }

  useEffect(() => {
    function handlePreviewMessage(event: MessageEvent) {
      if (!isPortalPreviewReadyMessage(event)) return
      setPreviewWindowReady(true)
      postPortalArticlePreviewPayload(previewWindowRef.current, artigo)
    }

    window.addEventListener('message', handlePreviewMessage)
    return () => window.removeEventListener('message', handlePreviewMessage)
  }, [artigo])

  useEffect(() => {
    if (previewWindowReady) sendPreviewPayload()
  }, [previewWindowReady, sendPreviewPayload])

  const handleSave = async () => {
    const validationErrors = validationProblems.filter((problem) => problem.severity === 'erro')
    if (validationErrors.length > 0) {
      handleProblemClick(validationErrors[0])
      notification.warning({
        message: 'Revise os problemas antes de salvar',
        description: 'Use o painel de problemas na sidebar para navegar até os campos destacados.',
      })
      return
    }

    setSaving(true)
    try {
      const artigoToSave = normalizePortalArtigoForSave(artigo)

      if (isNew) {
        const created = await api.PortalArtigos.create(artigoToSave)
        const savedArtigo = hydratePortalArtigo(created)
        setArtigo(savedArtigo)
        setTagsInput(formatTagsInput(created.tags))
        setSavedArtigoBaseline(savedArtigo)
        setPendingOptionalSectionCreations([])
        setSectionSaveErrors({})
        notification.success({ message: 'Artigo criado' })
        history.push(`/portal-artigos/${created.id}`)
      } else {
        const updated = await api.PortalArtigos.update(Number(params.artigoId), artigoToSave)
        const savedArtigo = hydratePortalArtigo(updated)
        setArtigo(savedArtigo)
        setTagsInput(formatTagsInput(updated.tags))
        setSavedArtigoBaseline(savedArtigo)
        setPendingOptionalSectionCreations([])
        setSectionSaveErrors({})
        notification.success({ message: 'Artigo salvo' })
      }
    } catch (error) {
      notification.error({ message: error.message || 'Não foi possível salvar o artigo' })
    } finally {
      setSaving(false)
    }
  }

  const handleSectionSave = async (target: PortalEditorSectionTarget) => {
    if (isNew || !artigo.id) return
    const sectionKey = getPortalEditorSectionKey(target)
    if (savingSectionKeysRef.current.size > 0) return

    const submittedDraft = artigo
    const expectedUpdatedAt = savedArtigoBaseline.updatedAt
    const value = getNormalizedPortalEditorSectionValue(submittedDraft, target)
    if (value === undefined || !expectedUpdatedAt) return

    savingSectionKeysRef.current.add(sectionKey)
    setSavingSectionKeys((currentKeys) => [...currentKeys, sectionKey])
    setSectionSaveErrors((currentErrors) => {
      const remainingErrors = { ...currentErrors }
      delete remainingErrors[sectionKey]
      return remainingErrors
    })
    try {
      const response = await api.PortalArtigos.updateEditorSection(
        artigo.id,
        target,
        value,
        expectedUpdatedAt
      )
      setSavedArtigoBaseline((currentBaseline) => ({
        ...applyPortalEditorSectionChanges(currentBaseline, response.changes),
        updatedAt: response.updatedAt,
      }))
      setArtigo((currentDraft) => ({
        ...mergeSavedChangesIntoDraft(currentDraft, submittedDraft, response.changes),
        updatedAt: response.updatedAt,
      }))
      notification.success({ message: 'Conteúdo salvo' })
    } catch (error) {
      const message = error.message || 'Não foi possível salvar o conteúdo'
      setSectionSaveErrors((currentErrors) => ({ ...currentErrors, [sectionKey]: message }))
      notification.error({ message })
    } finally {
      savingSectionKeysRef.current.delete(sectionKey)
      setSavingSectionKeys((currentKeys) =>
        currentKeys.filter((currentKey) => currentKey !== sectionKey)
      )
    }
  }

  const getSectionSaveControlProps = (
    target: PortalEditorSectionTarget
  ): PortalSectionSaveControlProps => {
    const sectionKey = getPortalEditorSectionKey(target)
    const isPendingCreation =
      target.scope === 'articleField' && pendingOptionalSectionCreations.includes(target.field)
    const persisted =
      !isNew && !isPendingCreation && portalEditorSectionExists(savedArtigoBaseline, target)
    return {
      disabled:
        loading ||
        saving ||
        savingSectionKeys.length > 0 ||
        !persisted ||
        !savedArtigoBaseline.updatedAt,
      dirty: persisted && isPortalEditorSectionDirty(artigo, savedArtigoBaseline, target),
      loading: savingSectionKeys.includes(sectionKey),
      error: sectionSaveErrors[sectionKey],
      onSave: () => handleSectionSave(target),
    }
  }

  const handleDelete = async () => {
    if (isNew || !artigo.id) return
    setSaving(true)
    try {
      await api.PortalArtigos.delete(artigo.id)
      notification.success({ message: 'Artigo removido' })
      history.push('/portal-artigos')
    } catch (error) {
      notification.error({ message: error.message || 'Não foi possível remover o artigo' })
    } finally {
      setSaving(false)
    }
  }

  const removeOptionalSection = (
    field: 'faq' | 'cta' | 'cursosRelacionados' | 'artigosRelacionados'
  ) => {
    const labels = {
      faq: 'FAQ',
      cta: 'CTA',
      cursosRelacionados: 'Cursos relacionados',
      artigosRelacionados: 'Artigos relacionados',
    }
    Modal.confirm({
      title: `Remover ${labels[field]}`,
      content: 'A remoção será efetivada somente quando o artigo inteiro for salvo.',
      okText: 'Remover',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: () => {
        setPendingOptionalSectionCreations((current) =>
          current.includes(field) ? current : [...current, field]
        )
        setArtigo((current) => {
          if (field === 'faq') {
            return {
              ...current,
              faq: null,
              conteudo: removeSummaryReferencesForAnchors(current.conteudo, [
                current.faq?.anchorId,
                current.faq?.sectionId,
              ]),
            }
          }
          if (field === 'cursosRelacionados') {
            return {
              ...current,
              cursosRelacionados: null,
              conteudo: removeSummaryReferencesForAnchors(current.conteudo, [
                current.cursosRelacionados?.anchorId,
              ]),
            }
          }
          if (field === 'artigosRelacionados') {
            return {
              ...current,
              artigosRelacionados: null,
              conteudo: removeSummaryReferencesForAnchors(current.conteudo, [
                current.artigosRelacionados?.anchorId,
              ]),
            }
          }
          return { ...current, cta: null }
        })
      },
    })
  }

  return (
    <PortalEditorTheme>
      <Layout>
        <PageHeader>
          <HeaderIdentity>
            <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/portal-artigos')}>
              Voltar
            </Button>
            <Title>
              {isNew ? (
                'Novo artigo'
              ) : (
                <>
                  <ArticleNumber>
                    Artigo #{params.artigoId}
                    {artigo.titulo ? ':' : ''}
                  </ArticleNumber>
                  {artigo.titulo ? ` ${artigo.titulo}` : ''}
                </>
              )}
            </Title>
          </HeaderIdentity>
          <HeaderActions>
            <Popover
              title="Blocos do artigo"
              trigger="click"
              placement="bottomRight"
              open={navigationOpen}
              onOpenChange={setNavigationOpen}
              content={
                <PortalArticleEditorOutline
                  blocks={artigo.conteudo || []}
                  onNavigateToBlock={navigateToBlock}
                  onNavigateToTopicItem={navigateToTopicItem}
                />
              }
            >
              <Button
                icon={<UnorderedListOutlined />}
                aria-haspopup="menu"
                aria-expanded={navigationOpen}
              >
                Navegar
              </Button>
            </Popover>
            <Button icon={<EyeOutlined />} onClick={handlePreview}>
              Visualizar
            </Button>
            {isNew && (
              <Button icon={<UploadOutlined />} onClick={() => setJsonImportModalOpen(true)}>
                Importar JSON
              </Button>
            )}
            {!isNew && (
              <Tooltip title="Inclui o conteúdo atualmente visível, mesmo que ainda não esteja salvo. O arquivo sempre será importado como rascunho.">
                <Button icon={<DownloadOutlined />} disabled={loading} onClick={handleDownloadJson}>
                  Baixar JSON
                </Button>
              </Tooltip>
            )}
            <SaveButton
              type="primary"
              onClick={handleSave}
              loading={saving || loading}
              disabled={loading || saving || savingSectionKeys.length > 0 || !hasUnsavedChanges}
              icon={
                !isNew && !loading && !saving && !hasUnsavedChanges ? <CheckOutlined /> : undefined
              }
            >
              {getSaveButtonLabel({ isNew, loading, saving, hasUnsavedChanges })}
            </SaveButton>
            {!isNew && (
              <Popconfirm
                title="Remover artigo"
                description="Tem certeza que deseja remover este artigo?"
                okText="Remover"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button danger loading={saving} icon={<DeleteOutlined />}>
                  Remover
                </Button>
              </Popconfirm>
            )}
          </HeaderActions>
        </PageHeader>

        <EditorGrid>
          <SideColumn>
            <PortalArticleSettingsSidebar
              artigo={artigo}
              isNew={isNew}
              tagsInput={tagsInput}
              expandedSections={expandedSettingsSections}
              openGraphModalOpen={openGraphModalOpen}
              onChange={updateArtigo}
              onTagsInputChange={(value) => {
                setTagsInput(value)
                updateArtigo({ tags: parseTagsInput(value) })
              }}
              onExpandedSectionsChange={setExpandedSettingsSections}
              onOpenGraphModalOpenChange={setOpenGraphModalOpen}
            />
            <PortalArticleProblemsPanel
              problems={validationProblems}
              onProblemClick={handleProblemClick}
            />
          </SideColumn>

          <MainColumn>
            <PortalArticleBlockEditor
              value={artigo.conteudo || []}
              onChange={(conteudo) => updateArtigo({ conteudo })}
              editingBlockIndexes={editingBlockIndexes}
              onEditingBlockIndexesChange={setEditingBlockIndexes}
              onBlockCreated={handleBlockCreated}
              isPillar={isPillar}
              isPublished={artigo.status === 'PUBLICADO'}
              getSectionSaveControlProps={getSectionSaveControlProps}
            />

            <OptionalContentCard
              id={portalEditorSectionIds.faq}
              title="FAQ"
              addLabel="Adicionar FAQ"
              configured={Boolean(artigo.faq)}
              expanded={isFaqExpanded}
              onExpandedChange={setIsFaqExpanded}
              onAdd={() => {
                updateArtigo({ faq: createDefaultFaq() })
                setPendingOptionalSectionCreations((current) =>
                  current.includes('faq') ? current : [...current, 'faq']
                )
                setIsFaqExpanded(true)
              }}
              onRemove={() => removeOptionalSection('faq')}
              saveControl={getSectionSaveControlProps({
                scope: 'articleField',
                field: 'faq',
              })}
            >
              <FaqEditor
                value={artigo.faq || undefined}
                onChange={(faq) => updateArtigo({ faq })}
                editingQuestionIndex={editingFaqItemIndex}
                onEditingQuestionChange={setEditingFaqItemIndex}
              />
            </OptionalContentCard>

            {!isPillar && (
              <OptionalContentCard
                id={portalEditorSectionIds.cta}
                title="CTA"
                addLabel="Adicionar CTA"
                configured={Boolean(artigo.cta)}
                expanded={isCtaExpanded}
                onExpandedChange={setIsCtaExpanded}
                onAdd={() => {
                  updateArtigo({ cta: createDefaultCta() })
                  setPendingOptionalSectionCreations((current) =>
                    current.includes('cta') ? current : [...current, 'cta']
                  )
                  setIsCtaExpanded(true)
                }}
                onRemove={() => removeOptionalSection('cta')}
                saveControl={getSectionSaveControlProps({
                  scope: 'articleField',
                  field: 'cta',
                })}
              >
                <CtaEditor
                  value={artigo.cta || undefined}
                  onChange={(cta) => updateArtigo({ cta })}
                />
              </OptionalContentCard>
            )}

            <OptionalContentCard
              id={portalEditorSectionIds.courses}
              title="Cursos relacionados"
              addLabel="Adicionar cursos relacionados"
              configured={Boolean(artigo.cursosRelacionados)}
              expanded={isCoursesExpanded}
              onExpandedChange={setIsCoursesExpanded}
              onAdd={() => {
                updateArtigo({ cursosRelacionados: createDefaultCourses() })
                setPendingOptionalSectionCreations((current) =>
                  current.includes('cursosRelacionados')
                    ? current
                    : [...current, 'cursosRelacionados']
                )
                setIsCoursesExpanded(true)
              }}
              onRemove={() => removeOptionalSection('cursosRelacionados')}
              saveControl={getSectionSaveControlProps({
                scope: 'articleField',
                field: 'cursosRelacionados',
              })}
            >
              <CoursesEditor
                value={artigo.cursosRelacionados || undefined}
                onChange={(cursosRelacionados) => updateArtigo({ cursosRelacionados })}
              />
            </OptionalContentCard>

            <OptionalContentCard
              id={portalEditorSectionIds.relatedArticles}
              title="Artigos relacionados"
              addLabel="Adicionar artigos relacionados"
              configured={Boolean(artigo.artigosRelacionados)}
              expanded={isRelatedArticlesExpanded}
              onExpandedChange={setIsRelatedArticlesExpanded}
              onAdd={() => {
                updateArtigo({ artigosRelacionados: createDefaultRelatedArticles() })
                setPendingOptionalSectionCreations((current) =>
                  current.includes('artigosRelacionados')
                    ? current
                    : [...current, 'artigosRelacionados']
                )
                setIsRelatedArticlesExpanded(true)
              }}
              onRemove={() => removeOptionalSection('artigosRelacionados')}
              saveControl={getSectionSaveControlProps({
                scope: 'articleField',
                field: 'artigosRelacionados',
              })}
            >
              <RelatedArticlesEditor
                value={artigo.artigosRelacionados || undefined}
                currentArticle={{ id: artigo.id, slug: artigo.slug }}
                onChange={(artigosRelacionados) => updateArtigo({ artigosRelacionados })}
              />
            </OptionalContentCard>
          </MainColumn>
        </EditorGrid>

        {isNew && (
          <PortalArticleJsonImportModal
            open={jsonImportModalOpen}
            onCancel={() => setJsonImportModalOpen(false)}
            onImport={handleImportJson}
          />
        )}
      </Layout>
    </PortalEditorTheme>
  )
}

function OptionalContentCard({
  id,
  title,
  addLabel,
  configured,
  expanded,
  onExpandedChange,
  onAdd,
  onRemove,
  saveControl,
  children,
}: {
  id: string
  title: string
  addLabel: string
  configured: boolean
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  onAdd: () => void
  onRemove: () => void
  saveControl: PortalSectionSaveControlProps
  children: React.ReactNode
}) {
  return (
    <ContentGroupCard
      id={id}
      title={title}
      className={expanded ? undefined : 'is-collapsed'}
      extra={
        configured ? (
          <Space size="small">
            <Button size="small" onClick={() => onExpandedChange(!expanded)}>
              {expanded ? 'Recolher' : 'Expandir'}
            </Button>
            <Button danger size="small" onClick={onRemove}>
              Remover
            </Button>
          </Space>
        ) : (
          <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>
            {addLabel}
          </Button>
        )
      }
    >
      {configured && expanded ? (
        <ContentEditorCard size="small">
          {children}
          <PortalSectionSaveButton {...saveControl} />
        </ContentEditorCard>
      ) : null}
    </ContentGroupCard>
  )
}

function getArticleFieldAndFocus(field: string) {
  const fieldId = articleFieldIds[field]
  if (!fieldId) return null

  const element = document.getElementById(fieldId) as HTMLElement | null
  if (!element) return null

  element.focus()
  return element
}

function getProblemSettingsSection(
  problem: PortalArticleProblem
): PortalArticleSettingsSection | null {
  if (problem.targetType === 'coverImage') return 'cover'
  if (problem.targetType === 'thumb') return 'thumb'
  if (problem.targetType === 'openGraph') return 'seo'
  if (problem.field?.startsWith('configuracaoPillar')) return 'pillar'
  if (problem.field && articleFieldIds[problem.field]) return 'article'
  return null
}

function getSaveButtonLabel({
  isNew,
  loading,
  saving,
  hasUnsavedChanges,
}: {
  isNew: boolean
  loading: boolean
  saving: boolean
  hasUnsavedChanges: boolean
}) {
  if (loading) return 'Carregando…'
  if (saving) return 'Salvando…'
  if (hasUnsavedChanges) return 'Salvar alterações'
  if (isNew) return 'Salvar artigo'
  return 'Salvo'
}

function getFaqItemIndexFromProblem(problem: PortalArticleProblem) {
  const match = problem.path?.match(/^faq\.items\.(\d+)/)
  if (!match) return undefined
  return Number(match[1])
}

function parseTagsInput(value: string) {
  return value
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

function formatTagsInput(tags?: string[]) {
  return (tags || []).join(', ')
}

function hydratePortalArtigo(artigo: PortalArtigoModel): PortalArtigoModel {
  return {
    ...createInitialArtigo(),
    ...artigo,
    imagemThumbUrl: artigo.imagemThumbUrl || '',
    imagemThumbAlt: artigo.imagemThumbAlt || '',
    tituloOg: artigo.tituloOg || '',
    descricaoOg: artigo.descricaoOg || '',
    imagemOgUrl: artigo.imagemOgUrl || '',
    imagemOgAlt: artigo.imagemOgAlt || '',
  }
}

function getArtigoSnapshot(artigo: PortalArtigoModel) {
  return JSON.stringify(sortObjectKeys(normalizePortalArtigoForSave(artigo)))
}

function sortObjectKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortObjectKeys)
  if (!value || typeof value !== 'object') return value

  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((sortedValue, key) => {
      sortedValue[key] = sortObjectKeys((value as Record<string, unknown>)[key])
      return sortedValue
    }, {})
}

const Layout = styled.div`
  align-self: flex-start;
  width: 100%;
  min-width: 0;
  min-height: 100%;
  box-sizing: border-box;
  padding: 32px;
  background: #fbfbfc;
`

const PageHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-width: 0;
  max-width: calc(100% + 64px);
  box-sizing: border-box;
  margin: -32px -32px 32px;
  padding: 14px 32px;
  border-bottom: 1px solid #e1e4ed;
  background: #fff;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    margin: -32px -32px 24px;
  }
`

const HeaderIdentity = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  align-items: center;
  gap: 16px;

  @media (max-width: 900px) {
    flex-basis: 100%;
  }
`

const HeaderActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;

  @media (max-width: 900px) {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`

const Title = styled.h1`
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  margin: 0;
  color: #0f172a;
  font-size: 24px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ArticleNumber = styled.span`
  font-weight: 700;
`

const SaveButton = styled(Button)`
  min-width: 156px;
`

const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  grid-template-areas: 'main side';
  gap: 20px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'side'
      'main';
  }
`

const MainColumn = styled.div`
  grid-area: main;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SideColumn = styled.div`
  grid-area: side;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ContentGroupCard = styled(Card)`
  overflow: hidden;
  border: 0;
  border-radius: 20px;
  background: #f4f4f4;
  color: #0f172a;
  font-weight: 600;

  > .ant-card-head {
    min-height: 68px;
    padding: 0 32px;
    border-bottom: 0;
    color: #0f172a;

    .ant-card-head-title {
      padding: 24px 0 20px;
      font-size: 18px;
      color: #0f172a;
      font-weight: 600;
    }
  }

  > .ant-card-body {
    padding: 0 32px 32px;
  }

  &.is-collapsed > .ant-card-body {
    display: none;
  }

  .ant-card {
    border-color: #e1e4ed;
    border-radius: 8px;
  }
`

const ContentEditorCard = styled(Card)`
  background: #fff;
`
